import React, { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useOAuthBanner } from './useOAuthBanner'
import TokenPopup from './TokenPopup'
import CredentialsPopup from './CredentialsPopup'
import { api } from '../api/client'
import { translateAuthError } from '../authMessages'
import { SETUP_GUIDE_LABEL, SETUP_GUIDE_URL } from '../helpLinks'
import { ROUTES } from '../routes'
import '../styles/dashboard.css'

// Layout авторизованной части: header + OAuth-баннер + содержимое роута (<Outlet/>).
// Навигация — через URL (react-router), без state-вкладок.
export default function Dashboard ({ user, setUser, onLogout }) {
  const navigate = useNavigate()
  const { banner, bannerMsg, dismiss } = useOAuthBanner(setUser)
  const [tokenPopupOpen, setTokenPopupOpen] = useState(false)
  const [passwordPopupOpen, setPasswordPopupOpen] = useState(false)
  const [resendState, setResendState] = useState(null)
  const fintabloConnected = Boolean(user.has_fintablo_token)

  async function handleResendVerification () {
    if (resendState === 'sending') return
    setResendState('sending')
    try {
      await api.resendEmailVerification()
      setResendState('Письмо отправлено. Проверьте почту.')
    } catch (err) {
      setResendState(translateAuthError(err))
    }
  }

  // Явный выход — не истёкшая сессия: точку возврата не запоминаем, иначе
  // следующий аккаунт после входа попал бы на страницу предыдущего.
  function handleLogout () {
    localStorage.removeItem('ch_token')
    navigate(ROUTES.LOGIN, { replace: true })
    onLogout()
  }

  return (
    <div className='dashboard'>
      <header className='dashboard__header'>
        <div className='dashboard__brand-group'>
          <a
            className='dashboard__back'
            href='https://my.fintablo.ru/'
            target='_blank'
            rel='noopener noreferrer'
            aria-label='Вернуться в Финтабло'
          >
            <img src='/fintablo-back-icon.svg' alt='' aria-hidden='true' />
          </a>
          <a
            className='dashboard__brand'
            href='https://my.fintablo.ru/'
            target='_blank'
            rel='noopener noreferrer'
            aria-label='Перейти в Финтабло'
          >
            <img className='dashboard__brand-logo' src='/fintablo-header-logo-v2.svg' alt='Финтабло' />
          </a>
        </div>
        <div className='dashboard__user-info'>
          <span
            className='dashboard__connection-status'
            data-connected={fintabloConnected}
          >
            {fintabloConnected ? 'Подключено' : 'Не подключено'}
          </span>
          <span className='dashboard__separator' aria-hidden='true'>|</span>
          {/* Смена пароля висит на самом адресе, а не на новой кнопке: раскладка
              шапки 360–375 px принята в CP-4 и ещё одной кнопки не вмещает. */}
          <button
            className='dashboard__email'
            onClick={() => setPasswordPopupOpen(true)}
            title='Сменить пароль'
            type='button'
          >
            {user.email}
          </button>
          <CredentialsPopup
            open={passwordPopupOpen}
            onClose={() => setPasswordPopupOpen(false)}
            user={user}
          />
          {/* Инструкция доступна с любого экрана после входа. Внутри iframe CRM
              ссылка обязана открываться новой вкладкой, иначе приложение
              подменяется статьёй. На узком экране остаётся иконка: раскладка
              шапки 360–375 px принята в CP-4 и текстовую ссылку не вмещает. */}
          <a
            className='dashboard__guide'
            href={SETUP_GUIDE_URL}
            target='_blank'
            rel='noopener noreferrer'
            title={SETUP_GUIDE_LABEL}
            aria-label={SETUP_GUIDE_LABEL}
          >
            <span className='dashboard__guide-text' aria-hidden='true'>{SETUP_GUIDE_LABEL}</span>
            <span className='dashboard__guide-icon' aria-hidden='true'>?</span>
          </a>
          <button className='dashboard__token-button' onClick={() => setTokenPopupOpen(true)}>
            Токен Финтабло
          </button>
          <TokenPopup
            open={tokenPopupOpen}
            onClose={() => setTokenPopupOpen(false)}
            onSaved={connected => setUser(current => ({ ...current, has_fintablo_token: connected }))}
            userId={user.id}
          />
          <button className='dashboard__logout-button' onClick={handleLogout}>Выйти</button>
        </div>
      </header>

      <main className='dashboard__main'>
        {/* Адрес подтверждается письмом. Пока он не подтверждён, доступ не
            ограничивается — только напоминание: у существующих пользователей
            адрес не проверялся никогда, и запирать их нельзя. */}
        {user.email_verified !== true && (
          <div className='dashboard__verify-banner' role='status'>
            <span>{`Подтвердите адрес — мы отправили письмо на ${user.email}`}</span>
            <button
              className='dashboard__verify-button'
              disabled={resendState === 'sending'}
              onClick={handleResendVerification}
              type='button'
            >
              Отправить ещё раз
            </button>
            {resendState && resendState !== 'sending' && (
              <span className='dashboard__verify-result'>{resendState}</span>
            )}
          </div>
        )}

        {banner === 'success' && (
          <div style={s.bannerSuccess}>
            {bannerMsg} успешно подключён.
            <button style={s.bannerClose} onClick={dismiss}>✕</button>
          </div>
        )}
        {banner === 'error' && (
          <div style={s.bannerError}>
            Ошибка подключения: {bannerMsg}
            <button style={s.bannerClose} onClick={dismiss}>✕</button>
          </div>
        )}

        <Outlet />
      </main>
    </div>
  )
}

const s = {
  bannerSuccess: { padding: '12px 16px', borderRadius: 10, background: '#ECFDF5', color: '#059669', fontSize: 13, fontWeight: 500, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  bannerError: { padding: '12px 16px', borderRadius: 10, background: '#FEF2F2', color: '#DC2626', fontSize: 13, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  bannerClose: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'inherit', opacity: 0.6, padding: 0 }
}
